import React, { useCallback, useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CONFIGS = {
  clients: {
    label: "Clientes",
    endpoint: "/clients",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "document", label: "CNPJ / CPF", type: "text" },
      { key: "email", label: "E-mail", type: "email" },
      { key: "phone", label: "Telefone", type: "text" },
      { key: "address", label: "Endereço", type: "text" },
    ],
    columns: [
      { key: "name", label: "Nome" },
      { key: "document", label: "Documento" },
      { key: "email", label: "E-mail" },
      { key: "phone", label: "Telefone" },
    ],
  },
  aircraft: {
    label: "Aeronaves",
    endpoint: "/aircraft",
    fields: [
      { key: "tail_number", label: "Prefixo", type: "text", required: true, uppercase: true },
      { key: "model", label: "Modelo", type: "text" },
      { key: "anac_code", label: "Código ANAC", type: "text" },
      { key: "client_name", label: "Cliente (nome)", type: "text" },
      { key: "default_commander_name", label: "Comandante padrão", type: "text" },
    ],
    columns: [
      { key: "tail_number", label: "Prefixo", mono: true },
      { key: "model", label: "Modelo" },
      { key: "anac_code", label: "ANAC" },
      { key: "client_name", label: "Cliente" },
    ],
  },
  pilots: {
    label: "Pilotos",
    endpoint: "/pilots",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "anac_code", label: "Código ANAC", type: "text" },
      { key: "phone", label: "Telefone", type: "text" },
      { key: "email", label: "E-mail", type: "email" },
    ],
    columns: [
      { key: "name", label: "Nome" },
      { key: "anac_code", label: "ANAC" },
      { key: "email", label: "E-mail" },
    ],
  },
  services: {
    label: "Serviços",
    endpoint: "/services",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "description", label: "Descrição", type: "text" },
      { key: "unit", label: "Unidade", type: "text", placeholder: "un / hora / litro" },
      { key: "unit_price", label: "Preço Unitário", type: "number" },
    ],
    columns: [
      { key: "name", label: "Nome" },
      { key: "unit", label: "Unidade" },
      { key: "unit_price", label: "Preço", money: true, align: "right" },
    ],
  },
};

export default function MasterData() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Master Data</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Cadastros</h1>
        <p className="text-sm text-slate-600 mt-1">Clientes, aeronaves, pilotos e serviços — base do autocomplete no intake.</p>
      </div>

      <Tabs defaultValue="aircraft">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1">
          {Object.entries(CONFIGS).map(([k, c]) => (
            <TabsTrigger
              key={k} value={k}
              className="rounded-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              data-testid={`md-tab-${k}`}
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(CONFIGS).map(([k, c]) => (
          <TabsContent key={k} value={k} className="mt-4">
            <CRUDPanel config={c} resource={k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CRUDPanel({ config, resource }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const canDelete = hasRole(user, "admin");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(config.endpoint);
    setItems(data);
    setLoading(false);
  }, [config.endpoint]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startCreate = () => {
    const empty = Object.fromEntries(config.fields.map((f) => [f.key, f.type === "number" ? 0 : ""]));
    setEditing({ __new: true, ...empty });
    setOpen(true);
  };

  const startEdit = (item) => {
    setEditing({ ...item });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...editing };
    delete payload.__new;
    delete payload.id;
    delete payload._id;
    delete payload.created_at;
    // Clean empty strings for optional fields to None
    for (const f of config.fields) {
      if (f.type === "email" && payload[f.key] === "") payload[f.key] = null;
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (f.uppercase && typeof payload[f.key] === "string") payload[f.key] = payload[f.key].toUpperCase();
    }
    try {
      if (editing.__new) {
        await api.post(config.endpoint, payload);
        toast.success("Criado com sucesso");
      } else {
        await api.put(`${config.endpoint}/${editing.id}`, payload);
        toast.success("Atualizado com sucesso");
      }
      setOpen(false);
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Excluir "${item.name || item.tail_number}"?`)) return;
    try {
      await api.delete(`${config.endpoint}/${item.id}`);
      toast.success("Excluído");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-600">{items.length} registros</div>
        <Button onClick={startCreate} className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid={`md-create-${resource}-button`}>
          <Plus className="w-4 h-4 mr-2" /> Novo
        </Button>
      </div>
      <Card className="rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto ag-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                {config.columns.map((c) => (
                  <th key={c.key} className={`py-3 px-4 font-semibold ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                ))}
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={config.columns.length + 1} className="py-8 text-center text-slate-400">Carregando…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={config.columns.length + 1} className="py-10 text-center text-slate-400">Nenhum registro.</td></tr>}
              {!loading && items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`md-row-${resource}-${item.id}`}>
                  {config.columns.map((c) => (
                    <td key={c.key} className={`py-3 px-4 ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono uppercase" : "text-slate-700"}`}>
                      {c.money ? brl(item[c.key]) : (item[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(item)} className="h-8 text-xs" data-testid={`md-edit-${resource}-${item.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => remove(item)} className="h-8 text-xs text-red-600 hover:text-red-700" data-testid={`md-delete-${resource}-${item.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing?.__new ? "Novo registro" : "Editar registro"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {editing && config.fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">{f.label}</Label>
                <Input
                  type={f.type}
                  required={f.required}
                  value={editing[f.key] ?? ""}
                  onChange={(e) => setEditing({ ...editing, [f.key]: f.type === "number" ? Number(e.target.value) : (f.uppercase ? e.target.value.toUpperCase() : e.target.value) })}
                  className={`h-11 rounded-sm ${f.uppercase ? "font-mono uppercase" : ""}`}
                  placeholder={f.placeholder}
                  data-testid={`md-field-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="md-save-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
